import React from "react"
import { useFetcher } from "react-router"
import { PlusIcon, TrashIcon, XCircleIcon, SaveIcon, LoaderCircleIcon, Pencil } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

import type { ActionResponse } from "@/utils/types"
import type { LoaderData } from "./brand-ui-config"
import {
  checkFieldPath,
  flattenSchema,
  getCapabilities,
  isBlockingStatus,
  isDateTimeOffsetType,
  isLiteralSelectType,
  type PathStatus,
  type SchemaField,
} from "@/utils/caseSearchSchema"
import {
  DURATION_UNITS,
  FILTER_DEFAULTS,
  FILTER_TYPES,
  buildFilterConfig,
  draftFromConfig,
  formatDuration,
  isFilterType,
  type FilterConfig,
  type FilterDraft,
  type FilterType,
} from "@/utils/caseSearchFilterConfig"

function isHiddenStatus(status: PathStatus): boolean {
  return status.kind === "valid" && status.field.isHidden
}

function isUnusablePath(status: PathStatus): boolean {
  return isBlockingStatus(status) || isHiddenStatus(status)
}

interface SearchField {
  fieldPath: string
  displayName: string
  defaultVisible: boolean
  filterable: boolean
  sortable: boolean
  searchable: boolean
  isCollection?: boolean
  filterConfig?: FilterConfig
}

export function CaseSearchTab({ config, data }: { config: SearchField[] | undefined; data: LoaderData }) {
  const [searchFields, setSearchFields] = React.useState(config ?? [])
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null)
  const [showAddDialog, setShowAddDialog] = React.useState(false)

  const fetcher = useFetcher<ActionResponse>()

  const schemaFields = React.useMemo(() => flattenSchema(data.searchSchema), [data.searchSchema])

  const addSearchField = (field: SearchField) => {
    setSearchFields((prev) => [...prev, field])
    setShowAddDialog(false)
  }

  const updateSearchField = (index: number, field: SearchField) => {
    setSearchFields((prev) => prev.map((f, i) => (i === index ? field : f)))
    setEditingIndex(null)
  }

  const removeSearchField = (index: number) => {
    setSearchFields((prev) => prev.filter((_, i) => i !== index))
  }

  if (config == null) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="mb-4 text-muted-foreground">No case search configuration found</p>
            <fetcher.Form method="post">
              <input type="hidden" name="action" value="ADD_SECTION" />
              <input type="hidden" name="organisation" value={data.loadedOrganisation} />
              <input type="hidden" name="brand" value={data.loadedBrand} />
              <input type="hidden" name="jsonPath" value="$.caseSearch" />
              <input
                type="hidden"
                name="value"
                value={JSON.stringify({
                  ...(data.config?.value ?? {}),
                  caseSearch: [],
                })}
              />
              <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
              <Button type="submit" disabled={fetcher.state !== "idle"}>
                {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Case Search Section
              </Button>
            </fetcher.Form>
            {fetcher.data?.errorMessage != null && (
              <p role="alert" className="mt-4 text-sm text-red-600">
                We couldn&apos;t add the Case Search section. Please try again. If the problem continues, contact
                support.
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  const invalidPathCount = searchFields.filter((f) => isUnusablePath(checkFieldPath(f.fieldPath, schemaFields))).length

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Case Search Configuration</h2>
            <Button onClick={() => setShowAddDialog(true)} variant="outline" size="sm" className="ml-auto">
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Search Field
            </Button>
          </div>

          {invalidPathCount > 0 && (
            <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              {invalidPathCount === 1
                ? "One configured field is unavailable."
                : `${invalidPathCount} configured fields are unavailable.`}{" "}
              Update or remove {invalidPathCount === 1 ? "it" : "them"} before saving so Case Search continues to work.
            </p>
          )}

          {searchFields.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-border py-16 text-center text-muted-foreground">
              <p className="text-lg">No search fields configured</p>
            </div>
          ) : (
            <div className="space-y-3">
              {searchFields.map((field, idx) => {
                const pathStatus = checkFieldPath(field.fieldPath, schemaFields)
                const pathHidden = isHiddenStatus(pathStatus)
                const pathInvalid = isUnusablePath(pathStatus)
                return (
                  <div key={idx} className={`rounded-lg border p-4 ${pathInvalid ? "border-red-300" : ""}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="mb-3 flex items-center gap-3">
                          <div>
                            <h3 className="text-lg font-semibold">{field.displayName}</h3>
                            <p className="text-sm text-muted-foreground">{field.fieldPath}</p>
                          </div>
                          {field.filterConfig?.order != null && (
                            <Badge variant="outline">Order: {field.filterConfig.order}</Badge>
                          )}
                          {pathInvalid && (
                            <Badge variant="outline" className="border-red-300 text-red-600">
                              {pathHidden ? "Unavailable" : "Field not found"}
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Checkbox checked={field.defaultVisible} className="pointer-events-none" />
                            <span className="text-foreground">Default Visible</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={field.searchable} className="pointer-events-none" />
                            <span className="text-foreground">Searchable</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={field.filterable} className="pointer-events-none" />
                            <span className="text-foreground">Filterable</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={field.sortable} className="pointer-events-none" />
                            <span className="text-foreground">Sortable</span>
                          </div>
                        </div>

                        {field.filterConfig != null && (
                          <div className="mt-3 rounded border p-3">
                            <p className="mb-2 text-sm font-semibold text-foreground">Filter Configuration</p>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              {field.filterConfig.type != null && (
                                <p>
                                  <span className="font-medium">Type:</span> {field.filterConfig.type}
                                </p>
                              )}
                              {field.filterConfig.description != null && (
                                <p>
                                  <span className="font-medium">Description:</span> {field.filterConfig.description}
                                </p>
                              )}
                              {field.filterConfig.maxLength != null && (
                                <p>
                                  <span className="font-medium">Max Length:</span> {field.filterConfig.maxLength}
                                </p>
                              )}
                              {field.filterConfig.isGroupEnd === true && (
                                <p>
                                  <span className="font-medium">Divider after this field:</span> Yes
                                </p>
                              )}
                              {field.filterConfig.options != null && field.filterConfig.options.length > 0 && (
                                <div>
                                  <p className="font-medium">Options:</p>
                                  <div className="mt-1 ml-2">
                                    {field.filterConfig.options.map((opt, optIdx) => (
                                      <p key={optIdx}>
                                        {opt.label ?? opt.name ?? opt.value}
                                        {opt.diff != null && ` (${formatDuration(opt.diff)})`}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="ml-4 flex gap-2">
                        <Button
                          type="button"
                          aria-label={`Edit ${field.displayName}`}
                          onClick={() => setEditingIndex(idx)}
                          variant="ghost"
                          size="sm"
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              aria-label={`Delete ${field.displayName}`}
                              variant="ghost"
                              size="sm"
                              className="cursor-pointer text-red-600 hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="end"
                            className="w-80 max-w-(--radix-popover-content-available-width) min-w-0"
                          >
                            <p className="text-sm">Delete column &quot;{field.displayName}&quot;?</p>
                            <div className="flex items-center justify-end gap-3 pt-4">
                              <PopoverClose asChild>
                                <Button type="button" variant="outline" size="sm" className="cursor-pointer">
                                  Cancel
                                </Button>
                              </PopoverClose>
                              <PopoverClose asChild>
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="cursor-pointer"
                                  onClick={() => removeSearchField(idx)}
                                >
                                  Delete
                                </Button>
                              </PopoverClose>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full gap-3">
          <fetcher.Form method="post" className="flex-1">
            <input type="hidden" name="action" value="SAVE_SECTION" />
            <input type="hidden" name="organisation" value={data.loadedOrganisation} />
            <input type="hidden" name="brand" value={data.loadedBrand} />
            <input type="hidden" name="jsonPath" value="$.caseSearch" />
            <input type="hidden" name="value" value={JSON.stringify(searchFields)} />
            <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
            <Button
              type="submit"
              disabled={fetcher.state !== "idle" || invalidPathCount > 0}
              className="w-full bg-info hover:bg-info/90"
            >
              {fetcher.state !== "idle" && fetcher.formData?.get("action") === "SAVE_SECTION" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Case Search
            </Button>
          </fetcher.Form>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle" && fetcher.formData?.get("action") === "REMOVE_SECTION" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <XCircleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Case Search Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire case search configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <fetcher.Form method="post">
                      <input type="hidden" name="action" value="REMOVE_SECTION" />
                      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                      <input type="hidden" name="brand" value={data.loadedBrand} />
                      <input type="hidden" name="jsonPath" value="$.caseSearch" />
                      <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
                      <Button type="submit" variant="destructive" size="sm" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Remove
                      </Button>
                    </fetcher.Form>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {showAddDialog && (
        <SearchFieldDialog
          key="add"
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onSave={addSearchField}
          schemaFields={schemaFields}
          existingFieldPaths={searchFields.map((field) => field.fieldPath)}
          title="Add Search Field"
        />
      )}

      {editingIndex !== null && (
        <SearchFieldDialog
          key={`edit-${editingIndex}`}
          open={true}
          onClose={() => setEditingIndex(null)}
          onSave={(field) => updateSearchField(editingIndex, field)}
          initialData={searchFields[editingIndex]}
          schemaFields={schemaFields}
          existingFieldPaths={searchFields.filter((_, index) => index !== editingIndex).map((field) => field.fieldPath)}
          title="Edit Search Field"
        />
      )}
    </div>
  )
}
function SearchFieldDialog({
  open,
  onClose,
  onSave,
  initialData,
  schemaFields,
  existingFieldPaths,
  title,
}: {
  open: boolean
  onClose: () => void
  onSave: (field: SearchField) => void
  initialData?: SearchField
  schemaFields: SchemaField[] | null
  existingFieldPaths: string[]
  title: string
}) {
  const [fieldPath, setFieldPath] = React.useState(initialData?.fieldPath ?? "")
  const [displayName, setDisplayName] = React.useState(initialData?.displayName ?? "")
  const [defaultVisible, setDefaultVisible] = React.useState(initialData?.defaultVisible ?? true)
  const [filterable, setFilterable] = React.useState(initialData?.filterable ?? false)
  const [sortable, setSortable] = React.useState(initialData?.sortable ?? false)
  const [searchable, setSearchable] = React.useState(initialData?.searchable ?? false)
  const [filterType, setFilterType] = React.useState(initialData?.filterConfig?.type ?? "")
  const filterDescription = initialData?.filterConfig?.description ?? ""
  const [filterHelpText, setFilterHelpText] = React.useState(initialData?.filterConfig?.helpText ?? "")
  const [filterIsGroupEnd, setFilterIsGroupEnd] = React.useState(initialData?.filterConfig?.isGroupEnd ?? false)
  const [formError, setFormError] = React.useState<string | null>(null)
  const editorRef = React.useRef<FilterTypeEditorHandle>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [dialogContainer, setDialogContainer] = React.useState<HTMLDivElement | null>(null)
  const setContentRef = React.useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node
    setDialogContainer(node)
  }, [])
  const [showPaths, setShowPaths] = React.useState(false)
  const [activePathIndex, setActivePathIndex] = React.useState(0)

  const pathListId = React.useId()
  const configuredPathSet = React.useMemo(() => new Set(existingFieldPaths), [existingFieldPaths])
  const matchingPaths =
    schemaFields
      ?.filter(
        (field) =>
          !field.isHidden &&
          !configuredPathSet.has(field.path) &&
          field.path.toLowerCase().includes(fieldPath.trim().toLowerCase()),
      )
      .slice(0, 50) ?? []
  const pathStatus = checkFieldPath(fieldPath, schemaFields)
  const isCollection =
    pathStatus.kind === "valid"
      ? pathStatus.field.isCollection
      : (initialData?.isCollection ?? FILTER_DEFAULTS.isCollection)
  const caps = getCapabilities(pathStatus)
  const pathBlocked = isBlockingStatus(pathStatus)
  const pathHidden = isHiddenStatus(pathStatus)
  const duplicateFieldPath = configuredPathSet.has(fieldPath.trim())
  const unknownSuggestions =
    pathStatus.kind === "unknown"
      ? pathStatus.suggestions.filter((suggestion) => !configuredPathSet.has(suggestion.path))
      : []

  const effFilterable = filterable && caps.filterable
  const effSortable = sortable && caps.sortable
  const effSearchable = searchable && caps.searchable

  const unsupportedType = filterType.length > 0 && !isFilterType(filterType)
  const dateRangeUnsupported =
    filterType === "date-range" && pathStatus.kind === "valid" && !isDateTimeOffsetType(pathStatus.field.type)
  const literalSelectUnsupported =
    filterType === "select" && pathStatus.kind === "valid" && !isLiteralSelectType(pathStatus.field.type)
  const canSave =
    fieldPath.trim().length > 0 &&
    displayName.trim().length > 0 &&
    !pathBlocked &&
    !pathHidden &&
    !duplicateFieldPath &&
    !unsupportedType &&
    !dateRangeUnsupported

  const handleSave = () => {
    if (!canSave) return

    const searchField: SearchField = {
      ...initialData,
      fieldPath: fieldPath.trim(),
      displayName: displayName.trim(),
      defaultVisible,
      filterable: effFilterable,
      sortable: effSortable,
      searchable: effSearchable,
    }
    delete searchField.filterConfig
    if (filterType.length === 0 && initialData?.filterConfig != null && !initialData.filterConfig.type) {
      searchField.filterConfig = initialData.filterConfig
    }

    if (pathStatus.kind === "valid") {
      if (isCollection) delete searchField.isCollection
      else searchField.isCollection = false
    } else if (initialData?.isCollection !== undefined) {
      searchField.isCollection = initialData.isCollection
    } else if (!isCollection) {
      searchField.isCollection = false
    }

    if (isFilterType(filterType)) {
      const draft = editorRef.current?.getDraft()
      if (draft == null) return
      const result = buildFilterConfig(
        filterType,
        {
          description: filterDescription,
          helpText: filterHelpText,
          isGroupEnd: filterIsGroupEnd,
        },
        draft,
        displayName,
        initialData?.filterConfig,
      )
      if (!result.ok) {
        setFormError(result.error)
        return
      }
      searchField.filterConfig = result.value
    }

    onSave(searchField)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        ref={setContentRef}
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          contentRef.current?.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure the index field path, display name and filter settings for a Case Search field.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium">Field Path *</label>
            <Popover open={showPaths && schemaFields != null} onOpenChange={setShowPaths}>
              <PopoverAnchor asChild>
                <Input
                  value={fieldPath}
                  onFocus={() => setShowPaths(true)}
                  onClick={() => setShowPaths(true)}
                  onChange={(e) => {
                    setFieldPath(e.target.value)
                    setActivePathIndex(0)
                    setShowPaths(true)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && showPaths) {
                      e.preventDefault()
                      e.stopPropagation()
                      setShowPaths(false)
                    } else if (e.key === "ArrowDown" && matchingPaths.length > 0) {
                      e.preventDefault()
                      setShowPaths(true)
                      setActivePathIndex((index) => (index + 1) % matchingPaths.length)
                    } else if (e.key === "ArrowUp" && matchingPaths.length > 0) {
                      e.preventDefault()
                      setShowPaths(true)
                      setActivePathIndex((index) => (index - 1 + matchingPaths.length) % matchingPaths.length)
                    } else if (e.key === "Enter" && showPaths && matchingPaths[activePathIndex] != null) {
                      e.preventDefault()
                      setFieldPath(matchingPaths[activePathIndex].path)
                      setShowPaths(false)
                    }
                  }}
                  autoComplete="off"
                  role={schemaFields != null ? "combobox" : undefined}
                  aria-autocomplete={schemaFields != null ? "list" : undefined}
                  aria-controls={schemaFields != null ? pathListId : undefined}
                  aria-expanded={schemaFields != null ? showPaths : undefined}
                  aria-activedescendant={
                    showPaths && matchingPaths.length > 0 ? `${pathListId}-${activePathIndex}` : undefined
                  }
                  aria-invalid={pathBlocked || pathHidden || duplicateFieldPath}
                  aria-describedby={duplicateFieldPath ? `${pathListId}-duplicate-error` : undefined}
                  placeholder="e.g., caseReference"
                />
              </PopoverAnchor>
              <PopoverContent
                container={dialogContainer}
                align="start"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onWheel={(event) => event.stopPropagation()}
                className="w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)] min-w-0 p-1"
              >
                <div
                  id={pathListId}
                  role="listbox"
                  className="max-h-60 overflow-y-auto overscroll-contain"
                  onWheel={(event) => event.stopPropagation()}
                >
                  {matchingPaths.length === 0 ? (
                    <p className="px-2 py-2 text-sm text-muted-foreground">No matching fields</p>
                  ) : (
                    matchingPaths.map((field, index) => (
                      <div
                        key={field.path}
                        id={`${pathListId}-${index}`}
                        role="option"
                        aria-selected={index === activePathIndex}
                        className="cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent aria-selected:bg-accent"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setFieldPath(field.path)
                          setShowPaths(false)
                        }}
                      >
                        <span className="block truncate font-medium">{field.path}</span>
                        <span className="block text-xs text-muted-foreground">{field.type}</span>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {duplicateFieldPath && (
              <p id={`${pathListId}-duplicate-error`} className="mt-1.5 text-xs text-red-600">
                This field is already configured. Choose a different field path.
              </p>
            )}
            {schemaFields == null && (
              <p className="mt-1.5 text-xs text-amber-700">
                Suggestions aren&apos;t available right now. Type the field name exactly as it appears in the system,
                including any capital letters.
              </p>
            )}
            {pathHidden && (
              <p className="mt-1.5 text-xs text-red-600">
                This field isn&apos;t available for searches. Choose a different field.
              </p>
            )}
            {pathStatus.kind === "case-mismatch" && (
              <p className="mt-1.5 text-xs text-red-600">
                Field names are case-sensitive.
                {!configuredPathSet.has(pathStatus.suggestion.path) && (
                  <>
                    {" "}
                    Did you mean{" "}
                    <button
                      type="button"
                      className="font-medium underline"
                      onClick={() => setFieldPath(pathStatus.suggestion.path)}
                    >
                      {pathStatus.suggestion.path}
                    </button>
                    ?
                  </>
                )}
              </p>
            )}
            {pathStatus.kind === "unknown" && (
              <div className="mt-1.5 text-xs text-red-600">
                <p>This field wasn&apos;t found. Choose a field from the list or check the spelling.</p>
                {unknownSuggestions.length > 0 && (
                  <p className="mt-1">
                    Suggested fields:{" "}
                    {unknownSuggestions.map((s, i) => (
                      <React.Fragment key={s.path}>
                        {i > 0 && ", "}
                        <button type="button" className="font-medium underline" onClick={() => setFieldPath(s.path)}>
                          {s.path}
                        </button>
                      </React.Fragment>
                    ))}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Display Name *</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Case Reference, Filename"
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Field Properties</h3>

            <label className="flex items-center gap-2">
              <Checkbox checked={defaultVisible} onCheckedChange={(checked) => setDefaultVisible(checked === true)} />
              <span className="text-sm">Default Visible</span>
            </label>

            <label className="flex items-center gap-2">
              <Checkbox
                checked={effFilterable}
                disabled={!caps.filterable}
                onCheckedChange={(checked) => setFilterable(checked === true)}
              />
              <span className="text-sm">Filterable</span>
              {!caps.filterable && <span className="text-xs text-gray-500">(not filterable in the index)</span>}
            </label>

            <label className="flex items-center gap-2">
              <Checkbox
                checked={effSearchable}
                disabled={!caps.searchable}
                onCheckedChange={(checked) => setSearchable(checked === true)}
              />
              <span className="text-sm">Searchable</span>
              {!caps.searchable && <span className="text-xs text-gray-500">(not searchable in the index)</span>}
            </label>

            <label className="flex items-center gap-2">
              <Checkbox
                checked={effSortable}
                disabled={!caps.sortable}
                onCheckedChange={(checked) => setSortable(checked === true)}
              />
              <span className="text-sm">Sortable</span>
              {!caps.sortable && <span className="text-xs text-gray-500">(not sortable in the index)</span>}
            </label>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Filter Configuration</h3>

            <div>
              <label htmlFor={`${pathListId}-filter-type`} className="mb-1 block text-sm font-medium text-gray-600">
                Type
              </label>
              <select
                id={`${pathListId}-filter-type`}
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterType.length > 0 ? filterType : NO_FILTER}
                onChange={(event) => {
                  const value = event.target.value
                  setFilterType(value === NO_FILTER ? "" : value)
                  setFormError(null)
                }}
              >
                <option value={NO_FILTER}>No filter</option>
                {FILTER_TYPES.map((type) => {
                  const dateRangeDisabled =
                    type.value === "date-range" &&
                    pathStatus.kind === "valid" &&
                    !isDateTimeOffsetType(pathStatus.field.type)
                  return (
                    <option key={type.value} value={type.value} disabled={dateRangeDisabled}>
                      {type.label}
                      {dateRangeDisabled ? " (date/time fields only)" : ""}
                    </option>
                  )
                })}
                {unsupportedType && (
                  <option value={filterType} disabled>
                    Unavailable filter type
                  </option>
                )}
              </select>
              {unsupportedType && (
                <p className="mt-1.5 text-xs text-red-600">
                  This filter option is no longer available. Choose a different filter type to continue.
                </p>
              )}
              {dateRangeUnsupported && pathStatus.kind === "valid" && (
                <p className="mt-1.5 text-xs text-red-600">
                  Date range filters can only be used with date and time fields. Choose a different filter type or
                  field.
                </p>
              )}
              {filterType.length === 0 && (
                <p className="mt-1.5 text-xs text-gray-500">
                  Without a filter type this field does not appear in the Viewer filter panel.
                </p>
              )}
            </div>

            {isFilterType(filterType) && (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-600">Help Text</label>
                  <Input
                    value={filterHelpText}
                    onChange={(e) => setFilterHelpText(e.target.value)}
                    placeholder="Shown below the filter label"
                  />
                </div>

                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={filterIsGroupEnd}
                    onCheckedChange={(checked) => setFilterIsGroupEnd(checked === true)}
                  />
                  <span className="text-sm">Show a divider after this field</span>
                </label>

                <FilterTypeEditor
                  key={filterType}
                  ref={editorRef}
                  type={filterType}
                  initial={initialData?.filterConfig}
                  allowLiteral={!literalSelectUnsupported}
                  onEdit={() => setFormError(null)}
                />
              </>
            )}
          </div>
        </div>

        <div className="space-y-2 border-t pt-4">
          {formError != null && (
            <p role="alert" className="text-sm text-red-600">
              {formError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={handleSave} disabled={!canSave} type="button">
              {initialData != null ? "Save" : "Add"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const NO_FILTER = "__none__"
const NO_DEFAULT = "__no_default__"
const dialogSelectClassName = "h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"

interface FilterTypeEditorHandle {
  getDraft: () => FilterDraft
}

function FilterTypeEditor({
  type,
  initial,
  allowLiteral = true,
  onEdit,
  ref,
}: {
  type: FilterType
  initial?: FilterConfig
  allowLiteral?: boolean
  onEdit: () => void
  ref: React.Ref<FilterTypeEditorHandle>
}) {
  const [draft, setDraft] = React.useState(() => draftFromConfig(initial, type))
  const editorId = React.useId()
  const parseAs: FilterDraft["parseAs"] = allowLiteral ? draft.parseAs : "string"
  React.useImperativeHandle(ref, () => ({ getDraft: () => ({ ...draft, parseAs }) }), [draft, parseAs])

  const patch = (changes: Partial<FilterDraft>) => {
    setDraft((prev) => ({ ...prev, ...changes }))
    onEdit()
  }

  const optionValues = draft.options.map((option) => String(option.value ?? "").trim()).filter((v) => v.length > 0)
  const staleDefaults = draft.selectDefaults.filter((value) => !optionValues.includes(value))

  switch (type) {
    case "text":
      return (
        <TypePanel>
          <LabelledInput
            label="Max Length"
            type="number"
            value={draft.maxLength}
            placeholder="e.g., 255"
            onChange={(maxLength) => patch({ maxLength })}
          />
          <LabelledInput
            label="Placeholder"
            value={draft.placeholder}
            onChange={(placeholder) => patch({ placeholder })}
          />
          <LabelledInput
            label="Default Value"
            value={draft.textDefault}
            onChange={(textDefault) => patch({ textDefault })}
          />
        </TypePanel>
      )

    case "number":
      return (
        <TypePanel>
          <LabelledInput label="Min" type="number" value={draft.min} onChange={(min) => patch({ min })} />
          <LabelledInput label="Max" type="number" value={draft.max} onChange={(max) => patch({ max })} />
          <LabelledInput
            label="Default Value"
            type="number"
            value={draft.numberDefault}
            onChange={(numberDefault) => patch({ numberDefault })}
          />
        </TypePanel>
      )

    case "autocomplete":
      return (
        <TypePanel>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={draft.autocompleteMultiple}
              onCheckedChange={(checked) => patch({ autocompleteMultiple: checked === true })}
            />
            <span className="text-sm">Allow Multiple Values</span>
          </label>
        </TypePanel>
      )

    case "time-range":
      return (
        <TypePanel>
          <p className="text-sm text-gray-600">
            Time range has no extra settings. The Viewer does not display this filter type yet.
          </p>
        </TypePanel>
      )

    case "boolean":
      return (
        <TypePanel>
          <LabelledInput
            label="True Label *"
            value={draft.trueLabel}
            placeholder="e.g., Yes"
            onChange={(trueLabel) => patch({ trueLabel })}
          />
          <LabelledInput
            label="False Label *"
            value={draft.falseLabel}
            placeholder="e.g., No"
            onChange={(falseLabel) => patch({ falseLabel })}
          />
          <LabelledInput
            label="Placeholder"
            value={draft.placeholder}
            onChange={(placeholder) => patch({ placeholder })}
          />
          <div>
            <label htmlFor={`${editorId}-boolean-default`} className="mb-1 block text-sm font-medium text-gray-600">
              Default Value
            </label>
            <select
              id={`${editorId}-boolean-default`}
              className={dialogSelectClassName}
              value={draft.booleanDefault === "" ? NO_DEFAULT : draft.booleanDefault}
              onChange={(event) =>
                patch({
                  booleanDefault: event.target.value === NO_DEFAULT ? "" : (event.target.value as "true" | "false"),
                })
              }
            >
              <option value={NO_DEFAULT}>No default</option>
              <option value="true">True{draft.trueLabel.trim() && ` (${draft.trueLabel.trim()})`}</option>
              <option value="false">False{draft.falseLabel.trim() && ` (${draft.falseLabel.trim()})`}</option>
            </select>
          </div>
        </TypePanel>
      )

    case "date-range":
      return (
        <TypePanel>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={draft.allowRange}
              onCheckedChange={(checked) => patch({ allowRange: checked === true })}
            />
            <span className="text-sm">Allow a range of dates</span>
            <span className="text-xs text-gray-500">(default: on, off means a single date)</span>
          </label>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Quick-pick Presets</label>
            <p className="text-xs text-gray-500">
              How far back from now each preset sets the range. One month is 4 weeks.
            </p>
            {draft.presets.map((preset, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  aria-label={`Preset ${idx + 1} name`}
                  value={preset.name}
                  placeholder="Name, e.g., Last 4 months"
                  onChange={(e) =>
                    patch({ presets: draft.presets.map((p, i) => (i === idx ? { ...p, name: e.target.value } : p)) })
                  }
                />
                <Input
                  aria-label={`Preset ${idx + 1} amount`}
                  type="number"
                  min={0}
                  className="w-24"
                  value={preset.amount}
                  onChange={(e) =>
                    patch({ presets: draft.presets.map((p, i) => (i === idx ? { ...p, amount: e.target.value } : p)) })
                  }
                />
                <select
                  aria-label={`Preset ${idx + 1} unit`}
                  className="h-9 w-36 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={preset.unit}
                  onChange={(event) =>
                    patch({
                      presets: draft.presets.map((p, i) =>
                        i === idx ? { ...p, unit: event.target.value as typeof p.unit } : p,
                      ),
                    })
                  }
                >
                  {DURATION_UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                  {preset.unit === "milliseconds" && <option value="milliseconds">Milliseconds</option>}
                </select>
                <Button
                  type="button"
                  aria-label={`Remove preset ${idx + 1}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => patch({ presets: draft.presets.filter((_, i) => i !== idx) })}
                >
                  <TrashIcon className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => patch({ presets: [...draft.presets, { name: "", amount: "", unit: "months" }] })}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Preset
            </Button>
          </div>
        </TypePanel>
      )

    case "select":
      return (
        <TypePanel>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">Options *</label>
            {draft.options.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  aria-label={`Option ${idx + 1} value`}
                  value={String(option.value ?? "")}
                  placeholder="Value"
                  onChange={(e) =>
                    patch({ options: draft.options.map((o, i) => (i === idx ? { ...o, value: e.target.value } : o)) })
                  }
                />
                <Input
                  aria-label={`Option ${idx + 1} label`}
                  value={option.label}
                  placeholder="Label (optional)"
                  onChange={(e) =>
                    patch({ options: draft.options.map((o, i) => (i === idx ? { ...o, label: e.target.value } : o)) })
                  }
                />
                <Button
                  type="button"
                  aria-label={`Remove option ${idx + 1}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => patch({ options: draft.options.filter((_, i) => i !== idx) })}
                >
                  <TrashIcon className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ))}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => patch({ options: [...draft.options, { value: "", label: "" }] })}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Option
              </Button>
            </div>
          </div>

          <label className="flex items-center gap-2">
            <Checkbox
              checked={draft.selectMultiple}
              onCheckedChange={(checked) =>
                patch({
                  selectMultiple: checked === true,
                  selectDefaults: checked === true ? draft.selectDefaults : draft.selectDefaults.slice(0, 1),
                })
              }
            />
            <span className="text-sm">Allow Multiple Selection</span>
          </label>

          <LabelledInput
            label="Empty Label"
            value={draft.emptyLabel}
            placeholder="e.g., -- Select --"
            onChange={(emptyLabel) => patch({ emptyLabel })}
          />

          <div>
            <label htmlFor={`${editorId}-parse-as`} className="mb-1 block text-sm font-medium text-gray-600">
              Send values as
            </label>
            <select
              id={`${editorId}-parse-as`}
              className={dialogSelectClassName}
              value={parseAs}
              onChange={(event) => patch({ parseAs: event.target.value as "literal" | "string" })}
            >
              <option value="string">Text, in quotes (default)</option>
              <option value="literal" disabled={!allowLiteral}>
                Literal, not in quotes (numbers, enums)
              </option>
            </select>
            {!allowLiteral && (
              <p className="mt-1.5 text-xs text-gray-500">String index fields must be sent as quoted text.</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor={`${editorId}-select-default`} className="text-sm font-medium text-gray-600">
              Default Value
            </label>
            {draft.selectMultiple ? (
              optionValues.length === 0 ? (
                <p className="text-xs text-gray-500">Add options to choose a default.</p>
              ) : (
                optionValues.map((value) => (
                  <label key={value} className="flex items-center gap-2">
                    <Checkbox
                      checked={draft.selectDefaults.includes(value)}
                      onCheckedChange={(checked) =>
                        patch({
                          selectDefaults:
                            checked === true
                              ? [...draft.selectDefaults, value]
                              : draft.selectDefaults.filter((d) => d !== value),
                        })
                      }
                    />
                    <span className="text-sm">{value}</span>
                  </label>
                ))
              )
            ) : (
              <select
                id={`${editorId}-select-default`}
                className={dialogSelectClassName}
                value={draft.selectDefaults[0] ?? NO_DEFAULT}
                onChange={(event) =>
                  patch({ selectDefaults: event.target.value === NO_DEFAULT ? [] : [event.target.value] })
                }
              >
                <option value={NO_DEFAULT}>No default</option>
                {[...new Set(optionValues)].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            )}
            {staleDefaults.length > 0 && (
              <p className="text-xs text-red-600">
                Default &quot;{staleDefaults.join('", "')}&quot; is no longer one of the options. Choose a listed option
                or{" "}
                <button type="button" className="underline" onClick={() => patch({ selectDefaults: [] })}>
                  clear the defaults
                </button>
                .
              </p>
            )}
          </div>
        </TypePanel>
      )
  }
}

function TypePanel({ children }: { children: React.ReactNode }) {
  return <div className="space-y-3 rounded-md border bg-gray-50 p-3">{children}</div>
}

function LabelledInput({
  label,
  value,
  onChange,
  type,
  placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
}) {
  const id = React.useId()
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-600">
        {label}
      </label>
      <Input id={id} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}
